import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, FileText, Clock, CheckCircle2, AlertCircle, Lock, Loader2, Download } from "lucide-react";
import { authApi, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const CAPACITY_BANDS = [
  "£25,000–£49,999", "£50,000–£99,999", "£100,000–£249,999",
  "£250,000–£499,999", "£500,000–£999,999", "£1,000,000 or more",
  "Institutional mandate — amount to be discussed",
];
const CATEGORIES = [
  { id: "hnw", label: "Certified High Net Worth Investor" },
  { id: "sophisticated", label: "Self-Certified Sophisticated Investor" },
  { id: "institutional", label: "Institutional / Professional Investor" },
];
const STEPS = ["Your details", "Classification", "Investment capacity", "Declarations & signature"];

const statusTone = {
  "Approved": "text-signal-green bg-signal-green/12 border-signal-green/30",
  "Under review": "text-signal-amber bg-signal-amber/12 border-signal-amber/30",
  "Further information requested": "text-signal-amber bg-signal-amber/12 border-signal-amber/30",
  "Rejected": "text-signal-red bg-signal-red/12 border-signal-red/30",
  "Access revoked": "text-signal-red bg-signal-red/12 border-signal-red/30",
  "Expired": "text-muted-foreground bg-white/5 border-white/15",
  "Draft": "text-muted-foreground bg-white/5 border-white/15",
};

export default function InvestorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [account, setAccount] = useState({ full_name: "", mobile: "", country: "", nationality: "", applicant_type: "Individual", company: "", job_title: "", source: "" });
  const [classification, setClassification] = useState({ category: "hnw", basis: "", explanation: "", none_apply: false });
  const [capacity, setCapacity] = useState({ band: CAPACITY_BANDS[0], exact: "", structure: "", timeframe: "", committee: "No", funds_available: "No", source_of_funds: "" });
  const [consents, setConsents] = useState({ declaration: false, risk: false, confidentiality: false, privacy: false, waiver: false });
  const [sig, setSig] = useState({ full_name: "", capacity: "Individual", job_title: "", authority: false });

  useEffect(() => { document.title = "Investor Dashboard | norse3"; }, []);

  const load = async () => {
    try {
      const [a, t] = await Promise.all([authApi.get("/portal/application"), authApi.get("/portal/templates")]);
      setApp(a.data); setTemplates(t.data);
      if (a.data.account) setAccount((s) => ({ ...s, ...a.data.account }));
      if (a.data.classification && Object.keys(a.data.classification).length) setClassification((s) => ({ ...s, ...a.data.classification }));
      if (a.data.capacity && Object.keys(a.data.capacity).length) setCapacity((s) => ({ ...s, ...a.data.capacity }));
    } catch (e) { toast.error("Could not load your application."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate("/investor"); };

  const saveSection = async (section, data) => {
    setSaving(true);
    try { const r = await authApi.put("/portal/application", { section, data }); setApp(r.data); toast.success("Saved"); }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const submit = async () => {
    if (classification.none_apply) return;
    if (!Object.values(consents).every(Boolean)) { toast.error("Please accept all declarations."); return; }
    if (!sig.full_name.trim()) { toast.error("A typed signature is required."); return; }
    setSaving(true);
    try {
      const r = await authApi.post("/portal/application/submit", { consents, signature: sig });
      setApp(r.data); toast.success("Application submitted for review.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-navy-950"><Loader2 className="h-8 w-8 text-primary animate-spin" /></div>;

  const submitted = app && !["Draft"].includes(app.status);
  const approved = app && app.status === "Approved";
  const tmpl = (k) => templates[k] || { title: k, body: "", version: "" };

  return (
    <div className="min-h-screen pt-16 bg-navy-950">
      <div className="mx-auto max-w-6xl px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-primary">Investor portal</p>
            <h1 className="mt-1 font-heading text-2xl font-bold text-foreground">Welcome, {user?.name || user?.email}</h1>
          </div>
          <Button variant="outline" onClick={doLogout} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
        </div>

        {/* Status card */}
        <div className="mt-6 rounded-lg border border-white/10 bg-navy-900/60 p-6 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Application status</p>
            <span className={`mt-1 inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${statusTone[app?.status] || statusTone.Draft}`}>{app?.status}</span>
          </div>
          {app?.expiry && approved && (
            <div className="text-sm text-muted-foreground"><Clock className="inline h-4 w-4 mr-1" /> Access valid until {new Date(app.expiry).toLocaleDateString("en-GB")}</div>
          )}
          {approved && <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/investor/data-room"><Lock className="mr-1.5 h-4 w-4" /> Open data room</Link></Button>}
        </div>

        {app?.status === "Rejected" && <Note tone="red" text="We are unable to provide access to the restricted investor materials based on the information currently available." />}
        {app?.status === "Further information requested" && <Note tone="amber" text="We need some further information to continue reviewing your application. A member of the team will be in touch, or please contact info@norse3.com." />}

        {!submitted && (
          <>
            <Stepper step={step} />
            <div className="mt-6 rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
              {step === 0 && (
                <Section title="Your details" onNext={() => { saveSection("account", account); setStep(1); }} saving={saving}>
                  <Grid>
                    <F label="Full legal name"><Input value={account.full_name} onChange={(e) => setAccount({ ...account, full_name: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Mobile number"><Input value={account.mobile} onChange={(e) => setAccount({ ...account, mobile: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Country of residence"><Input value={account.country} onChange={(e) => setAccount({ ...account, country: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Nationality"><Input value={account.nationality} onChange={(e) => setAccount({ ...account, nationality: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Applicant type">
                      <Select value={account.applicant_type} onValueChange={(v) => setAccount({ ...account, applicant_type: v })}>
                        <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/12"><SelectItem value="Individual">Individual</SelectItem><SelectItem value="Institutional">Institutional</SelectItem></SelectContent>
                      </Select>
                    </F>
                    <F label="Job title"><Input value={account.job_title} onChange={(e) => setAccount({ ...account, job_title: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    {account.applicant_type === "Institutional" && <F label="Company / organisation"><Input value={account.company} onChange={(e) => setAccount({ ...account, company: e.target.value })} className="bg-navy-800 border-white/12" /></F>}
                    <F label="How did you hear about norse3?"><Input value={account.source} onChange={(e) => setAccount({ ...account, source: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                  </Grid>
                </Section>
              )}

              {step === 1 && (
                <Section title="Investor classification" onBack={() => setStep(0)} onNext={() => { saveSection("classification", classification); setStep(2); }} nextDisabled={classification.none_apply} saving={saving}>
                  <F label="Which route applies to you?">
                    <Select value={classification.category} onValueChange={(v) => setClassification({ ...classification, category: v, none_apply: false })}>
                      <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/12">{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-sm font-semibold text-foreground">{tmpl(classification.category).title} <span className="text-xs text-muted-foreground">({tmpl(classification.category).version})</span></p>
                    <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{tmpl(classification.category).body}</p>
                  </div>
                  <div className="mt-4">
                    <F label={classification.category === "institutional" ? "Entity, regulatory status and intended legal basis" : "Applicable basis and a short factual explanation"}>
                      <Textarea rows={3} value={classification.explanation} onChange={(e) => setClassification({ ...classification, explanation: e.target.value })} className="bg-navy-800 border-white/12 resize-none" placeholder="Provide a brief, factual explanation." />
                    </F>
                  </div>
                  {classification.category === "sophisticated" && (
                    <label className="mt-3 flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={classification.none_apply} onCheckedChange={(v) => setClassification({ ...classification, none_apply: !!v })} className="mt-0.5 border-white/25" />
                      <span className="text-sm text-muted-foreground">None of the qualifying criteria apply to me.</span>
                    </label>
                  )}
                  {classification.none_apply && <Note tone="red" text="Based on the information provided, we cannot make the restricted investor materials available to you." />}
                </Section>
              )}

              {step === 2 && (
                <Section title="Indicative investment capacity" onBack={() => setStep(1)} onNext={() => { saveSection("capacity", capacity); setStep(3); }} saving={saving}>
                  <F label="How much capital could you realistically consider investing in norse3?">
                    <Select value={capacity.band} onValueChange={(v) => setCapacity({ ...capacity, band: v })}>
                      <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/12">{CAPACITY_BANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <Grid>
                    <F label="Exact indicative amount (optional)"><Input value={capacity.exact} onChange={(e) => setCapacity({ ...capacity, exact: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Preferred structure (if known)"><Input value={capacity.structure} onChange={(e) => setCapacity({ ...capacity, structure: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Expected decision timeframe"><Input value={capacity.timeframe} onChange={(e) => setCapacity({ ...capacity, timeframe: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Investment committee approval required?">
                      <Select value={capacity.committee} onValueChange={(v) => setCapacity({ ...capacity, committee: v })}>
                        <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/12"><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
                      </Select>
                    </F>
                    <F label="Are funds currently available?">
                      <Select value={capacity.funds_available} onValueChange={(v) => setCapacity({ ...capacity, funds_available: v })}>
                        <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/12"><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
                      </Select>
                    </F>
                    <F label="General source-of-funds category"><Input value={capacity.source_of_funds} onChange={(e) => setCapacity({ ...capacity, source_of_funds: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                  </Grid>
                  <Note tone="cyan" text="This is a non-binding indication of potential investment capacity. It is not a commitment, subscription or offer to invest." />
                </Section>
              )}

              {step === 3 && (
                <Section title="Declarations & electronic signature" onBack={() => setStep(2)} onSubmit={submit} saving={saving}>
                  <div className="space-y-3">
                    {["risk", "confidentiality", "waiver"].map((k) => (
                      <div key={k} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                        <p className="text-sm font-semibold text-foreground">{tmpl(k).title} <span className="text-xs text-muted-foreground">({tmpl(k).version})</span></p>
                        <p className="mt-1.5 text-sm text-muted-foreground whitespace-pre-line">{tmpl(k).body}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2.5">
                    {[["declaration", "I confirm my investor declaration is accurate and complete."], ["risk", "I have read and understand the risk warning."], ["confidentiality", "I accept the confidentiality undertaking."], ["privacy", "I have read the privacy notice."], ["waiver", "I accept the investor acknowledgement and waiver."]].map(([k, label]) => (
                      <label key={k} className="flex items-start gap-3 cursor-pointer">
                        <Checkbox checked={consents[k]} onCheckedChange={(v) => setConsents({ ...consents, [k]: !!v })} className="mt-0.5 border-white/25" />
                        <span className="text-sm text-muted-foreground">{label}</span>
                      </label>
                    ))}
                  </div>
                  <Grid>
                    <F label="Full typed name (signature)"><Input value={sig.full_name} onChange={(e) => setSig({ ...sig, full_name: e.target.value })} className="bg-navy-800 border-white/12" /></F>
                    <F label="Signing capacity">
                      <Select value={sig.capacity} onValueChange={(v) => setSig({ ...sig, capacity: v })}>
                        <SelectTrigger className="bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/12"><SelectItem value="Individual">Individual</SelectItem><SelectItem value="Entity signatory">Entity signatory</SelectItem></SelectContent>
                      </Select>
                    </F>
                    {sig.capacity === "Entity signatory" && <F label="Job title"><Input value={sig.job_title} onChange={(e) => setSig({ ...sig, job_title: e.target.value })} className="bg-navy-800 border-white/12" /></F>}
                  </Grid>
                  {sig.capacity === "Entity signatory" && (
                    <label className="mt-3 flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={sig.authority} onCheckedChange={(v) => setSig({ ...sig, authority: !!v })} className="mt-0.5 border-white/25" />
                      <span className="text-sm text-muted-foreground">I confirm I have authority to sign on behalf of the entity.</span>
                    </label>
                  )}
                  <Note tone="cyan" text="Your signature, the exact wording and version signed, the date/time, your account reference and technical metadata are recorded and hashed for audit." />
                </Section>
              )}
            </div>
          </>
        )}

        {submitted && app.signature && (
          <div className="mt-6 rounded-lg border border-white/10 bg-navy-900/60 p-6">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-signal-green" /> Signed declaration recorded</p>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2 text-sm">
              <div><dt className="text-muted-foreground">Signatory</dt><dd className="text-foreground">{app.signature.full_name}</dd></div>
              <div><dt className="text-muted-foreground">Category</dt><dd className="text-foreground">{(app.classification || {}).category}</dd></div>
              <div><dt className="text-muted-foreground">Indicative capacity</dt><dd className="text-foreground">{(app.capacity || {}).band}</dd></div>
              <div><dt className="text-muted-foreground">Document hash</dt><dd className="text-foreground font-mono text-xs break-all">{app.signature.hash}</dd></div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="mt-8 flex flex-wrap gap-2">
      {STEPS.map((s, i) => (
        <div key={s} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${i === step ? "border-primary/40 bg-primary/12 text-foreground" : i < step ? "border-white/10 text-signal-green" : "border-white/10 text-muted-foreground"}`}>
          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[0.7rem] ${i <= step ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>{i + 1}</span>
          <span className="hidden sm:inline">{s}</span>
        </div>
      ))}
    </div>
  );
}
function Grid({ children }) { return <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>; }
function F({ label, children }) { return <div><Label className="text-sm text-foreground">{label}</Label><div className="mt-1.5">{children}</div></div>; }
function Section({ title, children, onBack, onNext, onSubmit, nextDisabled, saving }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      {children}
      <div className="mt-6 flex gap-3">
        {onBack && <Button variant="outline" onClick={onBack} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">Back</Button>}
        {onNext && <Button onClick={onNext} disabled={nextDisabled} className="bg-primary text-primary-foreground hover:bg-primary/90">Save & continue</Button>}
        {onSubmit && <Button onClick={onSubmit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Sign & submit</Button>}
      </div>
    </div>
  );
}
function Note({ tone, text }) {
  const tones = { red: "border-signal-red/30 bg-signal-red/[0.06] text-signal-red", amber: "border-signal-amber/30 bg-signal-amber/[0.06] text-signal-amber", cyan: "border-primary/25 bg-primary/[0.06] text-muted-foreground" };
  return <div className={`mt-4 flex items-start gap-2 rounded-xl border p-4 text-sm ${tones[tone]}`}><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{text}</span></div>;
}
